import type { CreateUpdateOrderDTO, OrderDTO } from 'ecosystem'
import { createUrl } from 'shared-utils'
import {
  createEndpointError,
  createError,
  fetchPropsFactory,
  storefrontApiCall
} from '../../../../utils/fetching'
import { ORDERS_ENDPOINT } from '../../constants'
import { alertService } from '../../../../utils/alert-service'
import { isOrderAlreadyProvisioned } from '../utils'
import { type CompleteStorefrontOrderProps } from './types'
import { klarnaPaymentProvider } from './klarnaPaymentProvider'
import { nexiPaymentProvider } from './nexiPaymentProvider'
import { sveaPaymentProvider } from './sveaPaymentProvider'
import { qliroPaymentProvider } from './qliroPaymentProvider'
import { briqpayPaymentProvider } from './briqpayPaymentProvider'
import { generateBasePayload } from './utils'

export async function completeStorefrontOrder({
  order: oldOrder,
  paymentProviderInfo,
  shippingProviderInfo
}: CompleteStorefrontOrderProps) {
  // Defensive idempotency guard: never issue a second PUT for an order that was already provisioned
  // (concurrent confirmation page + payment push, or a push retry). Providers add their own guard
  // before this point; this protects any other caller too.
  if (isOrderAlreadyProvisioned(oldOrder)) {
    return oldOrder
  }

  let payload: CreateUpdateOrderDTO = generateBasePayload(oldOrder)

  switch (paymentProviderInfo?.providerName ?? 'KLARNA') {
    case 'KLARNA':
      payload = klarnaPaymentProvider({
        order: oldOrder,
        paymentProviderInfo,
        shippingProviderInfo
      })
      break

    case 'NEXI':
      payload = nexiPaymentProvider({
        order: oldOrder,
        paymentProviderInfo,
        shippingProviderInfo
      })
      break

    case 'SVEA':
      payload = sveaPaymentProvider({
        order: oldOrder,
        paymentProviderInfo,
        shippingProviderInfo
      })
      break

    case 'QLIRO':
      payload = qliroPaymentProvider({
        order: oldOrder,
        paymentProviderInfo,
        shippingProviderInfo
      })
      break

    case 'BRIQPAY':
      payload = briqpayPaymentProvider({
        order: oldOrder,
        paymentProviderInfo,
        shippingProviderInfo
      })
      break

    case 'LEDYER': {
      throw createError('Not implemented completeStorefrontOrder for LEDYER case')
    }
  }

  const props = fetchPropsFactory({
    props: {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify(payload)
    }
  })

  // update order endpoint returns OrderDTO included items
  const { data: updateOrderResult, error: updateOrderError } = await storefrontApiCall<OrderDTO>(
    createUrl(ORDERS_ENDPOINT, { includeItems: true, includeCustomer: false }),
    props
  )

  if (updateOrderError || !updateOrderResult || !updateOrderResult.orderNumber) {
    void alertService.send('Provision order | Complete storefront order', {
      blocks: [
        {
          title: 'Payload',
          content: payload
        },
        {
          title: 'Error',
          content: updateOrderError
        }
      ]
    })

    return createEndpointError('complete storefront order', updateOrderError)
  }

  return updateOrderResult
}
